/**
 * Structured, authoritative per-squadron knowledge that drives the squadron
 * pages (supplementing the narrative prose in squadrons.js). Keyed by slug so it
 * maps 1:1 to squadrons.js AND to a single intake-form response per squadron.
 *
 * CommonJS (module.exports, no import syntax) so both React and the pre-render
 * script can consume it, same as squadrons.js.
 *
 * Fill policy:
 *   - Fields we already know (role, missions) are PRE-FILLED as drafts and marked
 *     `_confirmed: false` until a squadron lead signs off.
 *   - Lead-collected fields (recurring, quals, activeMembers, culture, pitch)
 *     start empty and get pasted in from form responses.
 *   - Notable operations are task-force-wide (same for every squadron), so they
 *     live in a shared list, NOT per squadron here.
 *   - An empty field renders nothing on the page. We never invent content.
 *
 * Entry shape:
 *   role:          string   // "What is your primary / most effective role?" (one line)
 *   missions:      string[] // "What are all the roles you can perform?" (subset of MISSION_TAGS)
 *   recurring:     [{ name, cadence }]  // "Any weekly/recurring training or events?"
 *   quals:         string[] // qualifications members can earn (if any)
 *   activeMembers: string   // rough estimate of active regular members
 *   culture:       { nickname, motto, vibe }
 *   pitch:         string   // "Anything you'd want new recruits to know about this squadron?"
 *   _confirmed:    boolean  // true once a lead has reviewed this entry
 *
 * ---------------------------------------------------------------------------
 * HOUSE VOICE — apply to every response, including future ones.
 *
 * The raw survey answers mixed first and third person, ran from sentence
 * fragments to full paragraphs, and varied ~6x in length. Rendered as a set,
 * that reads like eleven different authors. Normalize the voice; never the facts.
 *
 *   - Third person, squadron as subject. "The 472nd flies...", "VMGR-251
 *     provides..." — never "we" or "you". This is the single biggest normalizer.
 *   - Present tense. Complete sentences with terminal punctuation.
 *   - Neutral register: state capability plainly. No boasting, no slang, no
 *     in-jokes, no exclamation marks. Where a lead's phrasing was promotional,
 *     keep the substance and drop the sell.
 *
 * Field shapes:
 *   - `role` is one line, a noun phrase, sentence case. No em dashes, slashes,
 *     or clause chains. Acronyms uppercase.
 *   - `missions` uses MISSION_TAGS vocabulary only, listed in MISSION_TAGS order.
 *   - `recurring` splits free-text answers into { name, cadence } pairs; times are
 *     normalized to "Days, H:MM AM/PM Zone".
 *   - `quals` is a list, sentence case, one qualification per item.
 *   - `activeMembers` uses an en dash for ranges.
 *   - `culture.motto` is VERBATIM — never translated or rewritten. A motto is
 *     named data, not prose.
 *   - `culture.vibe` is exactly one third-person sentence.
 *   - `pitch` is two to three sentences following one arc: what the squadron does
 *     for a new pilot, then what it expects of them.
 *
 * The line that does not move: voice changes, facts do not. Nothing is added that
 * a lead did not say, in this field or in another of their own answers. Where a
 * thin answer is expanded, every added fact comes from that same lead's other
 * responses — see vf-172 below for the worked example.
 * ---------------------------------------------------------------------------
 *
 * Source: "JTF Heavy — Squadron Knowledge (Responses)" sheet. Lead callsigns stay
 * in the sheet and are deliberately not recorded here (reference only, never published).
 * Full normalization record and per-squadron rationale: /SQUADRON_CONTENT.md
 */

// Controlled vocabulary for `missions` — also the checkbox options on the form.
// Keep in sync with tools/create-squadron-form.gs.
//
// "Air assault (troop insertion)" is spelled out because the bare doctrinal term
// reads as air-to-air to anyone who doesn't know it — and these tags render as
// standalone chips, with no surrounding prose to disambiguate them. It means
// moving ground troops to an objective by helicopter; it is an air-to-ground
// mission and implies no air-to-air capability.
const MISSION_TAGS = [
  'SEAD/DEAD', 'CAS', 'Strike', 'Anti-ship', 'CAP', 'Intercept', 'Escort',
  'Recon', 'Air assault (troop insertion)', 'CASEVAC', 'Tanker', 'Transport',
  'Airdrop', 'Aggressor / red air', 'DACT', 'Demonstration', 'Logistics',
];

const empty = () => ({
  recurring: [],     // [{ name, cadence }] — weekly/recurring training & events
  quals: [],         // qualifications members can earn (if any)
  activeMembers: '', // rough estimate of active regular members
  culture: { nickname: '', motto: '', vibe: '' },
  pitch: '',         // anything they'd want new recruits to know
  _confirmed: false,
});

// Draft entry for a squadron whose lead hasn't returned the intake form: role and
// missions pre-filled from squadrons.js, every lead-collected field left empty.
//
// Object.assign rather than object spread, matching squadrons.js. Object spread
// makes Babel pull in a @babel/runtime interop helper, which defeats webpack's
// CommonJS named-export analysis and breaks `import { KNOWLEDGE }` downstream.
const draft = (role, missions) => Object.assign({ role, missions }, empty());

// Shared pitch for any squadron whose lead hasn't returned the intake form.
// Signed off 2026-09-10. Deliberately makes no squadron-specific claim — it is
// task-force-wide and therefore true on every page. Per-squadron `pitch` stays
// empty in the data (no lead said this); the page substitutes this at render.
const FALLBACK_PITCH =
  'Joint Task Force Heavy flies together across every mission set, and this squadron ' +
  'trains and operates as part of that wider force. New pilots are welcome regardless of ' +
  'experience; the squadron teaches the airframe, the tactics, and the radio work from ' +
  'the ground up.';

const KNOWLEDGE = {
  '472nd-f-16-viper': {
    role: 'SEAD and combat air patrol',
    missions: ['SEAD/DEAD', 'CAS', 'Strike', 'CAP', 'Intercept', 'Escort', 'Recon'],
    recurring: [
      {
        name: 'Squadron training — A/A refueling, CAP, SEAD, group flights, and search and destroy',
        cadence: 'Sundays, 4:00 PM Mountain',
      },
    ],
    quals: ['Flight instructor', 'Planner'],
    activeMembers: '2–4',
    culture: {
      nickname: 'Pit Vipers',
      // The submitted motto was dropped 2026-09-10 as unsuitable for a public
      // recruitment page. Deliberately not quoted here — this file ships a source
      // map in production, so comments are publicly readable. Rationale and the
      // original wording are in /SQUADRON_CONTENT.md, which is not deployed.
      motto: '',
      vibe: 'A relaxed, light-hearted squadron that takes skill development seriously.',
    },
    pitch:
      'The 472nd welcomes questions from pilots at any experience level and puts a strong ' +
      'emphasis on learning. Recruits are asked to attend two trainings or missions each ' +
      'month, and to complete BCF training within their first two months.',
    _confirmed: true,
  },
  '484th-a-10-warthog': draft(
    'Close air support and battlefield interdiction',
    ['CAS', 'Strike'],
  ),
  '584th-f-4-phantom': draft(
    'Intercept and air-to-ground strike',
    ['Strike', 'Intercept', 'Escort'],
  ),
  'vmfa-390-f-18-hornet': {
    role: 'Multirole SEAD and strike',
    missions: [
      'SEAD/DEAD', 'CAS', 'Strike', 'Anti-ship', 'CAP', 'Intercept', 'Escort',
      'Recon', 'Aggressor / red air', 'Demonstration',
    ],
    recurring: [{ name: 'Flight night', cadence: 'Thursdays and Sundays' }],
    quals: ['CASE I / III', 'Flight lead upgrade', 'Flight instructor I / II'],
    activeMembers: '7',
    culture: {
      nickname: 'Sea Witches',
      motto: 'Victoria per vim',
      vibe: 'A relaxed squadron that stays focused on the mission.',
    },
    pitch:
      'VMFA-390 expects attendance rather than prior skill, and brings new pilots along ' +
      "from wherever they start. The squadron's focus is on learning and developing together.",
    _confirmed: true,
  },
  '142nd-assault-helicopter': {
    // Lead typed "Ground assault with troops" in free text but ticked the
    // "Air assault" checkbox; the checkbox is the doctrinally correct one and
    // wins. Air assault = helicopter-borne troop insertion, air-to-ground. The
    // 142nd flies no A2A — no CAP, Intercept, or Escort tag is set.
    role: 'Air assault, anti-armor, and rotary-wing reconnaissance',
    missions: [
      'SEAD/DEAD', 'Strike', 'Recon', 'Air assault (troop insertion)', 'CASEVAC', 'Logistics',
    ],
    recurring: [
      { name: 'Squadron training', cadence: 'Twice a week' },
      { name: 'Gunnery qualification', cadence: 'Semi-annual' },
    ],
    quals: ['Flight lead', 'Ship landing', 'Air mission commander'],
    activeMembers: '4–8',
    culture: {
      nickname: 'Aces',
      motto: '',
      vibe: 'A relaxed squadron that turns professional when tactics and strategy are involved.',
    },
    // Apache/Kiowa detail came out of the lead's original one-line role answer;
    // moved here so `role` could take the standard noun-phrase shape.
    pitch:
      'The 142nd teaches and flies nearly every rotary-wing mission available in DCS — ' +
      'inserting troops by air, hunting armor in the Apache, and scouting in the Kiowa. ' +
      'Training runs twice a week, with a gunnery qualification twice a year.',
    _confirmed: true,
  },
  'vmgr-251-c-130-hercules': draft(
    'Aerial refueling and tactical transport',
    ['Tanker', 'Transport', 'Airdrop'],
  ),
  'vfa-119-f-18-hornet': {
    role: 'Multirole SEAD and precision strike',
    missions: ['SEAD/DEAD', 'Strike', 'Anti-ship', 'CAP', 'Recon'],
    recurring: [{ name: 'Flight night', cadence: 'Tuesdays, 6:30 PM PST' }],
    quals: [
      'Carrier qualified',
      'Flight lead',
      // "I & II" as submitted; normalized to "I / II" to match VMFA-390's
      // wording for the same qualification, per the quals field shape above.
      'Flight instructor I / II',
      'Advanced flight lead',
      'A/A refueling',
    ],
    activeMembers: '6–10',
    culture: {
      nickname: 'Tritons',
      motto: 'Iram Maris — Wrath from the Sea',
      vibe: 'A close-knit squadron that operates as a single unit.',
    },
    // Submitted pitch was the most promotional of the six ("First to the fight,
    // last to leave..."). Claim preserved, sell dropped.
    pitch:
      'VFA-119 takes on the missions other squadrons cannot or will not fly, and stays ' +
      'with them through to the end. Carrier qualification, flight lead, and instructor ' +
      'tracks are all open to members.',
    _confirmed: true,
  },
  'vf-172-f-14-tomcat': {
    // Worked example of expanding a thin answer. Submitted role was the single
    // word "CAP"; submitted pitch was "Come ready to learn and be ready to learn"
    // (7 words, against the 472nd's 45). Every fact below comes from another of
    // THIS lead's own answers: airframe from squadrons.js, mission list from
    // `missions`, cadence and carrier ops from `recurring`, open-quals from
    // `quals`. Carrier operations were promoted into `role` because the lead
    // volunteered them unprompted as a defining activity. Nothing new introduced.
    role: 'Combat air patrol and carrier operations',
    missions: ['Strike', 'CAP', 'Intercept', 'Escort', 'Recon', 'Aggressor / red air'],
    recurring: [
      { name: 'Squadron flying — CAP and carrier operations', cadence: 'At least every other day' },
    ],
    // The lead's answer here was a policy ("all members can earn any role if
    // they so desire"), not a list of qualifications. `quals` is modelled as a
    // list of named quals, so the policy lives in `pitch` instead — where the
    // lead's meaning is preserved in full — and this stays empty.
    quals: [],
    activeMembers: '4',
    culture: {
      nickname: '',
      motto: '',
      vibe: 'The squadron leans toward the mil-sim end of the spectrum.',
    },
    pitch:
      'VF-172 flies combat air patrol in the F-14B, with strike, intercept, escort, ' +
      'reconnaissance, and red air alongside it. The squadron is airborne at least every ' +
      'other day, working CAP on every flight and carrier operations on most, and any ' +
      'qualification is open to a member who pursues it. New pilots are expected to arrive ' +
      'ready to learn.',
    _confirmed: true,
  },
  'blue-tide-demo-team': {
    role: 'Precision flight demonstration',
    missions: ['Demonstration'],
    recurring: [{ name: 'Team practice', cadence: 'Mondays, 7:45 PM Pacific' }],
    quals: [],
    activeMembers: '3–4, recruiting two more',
    culture: {
      nickname: 'Blue Tide',
      motto: 'Studio Excelsior',
      vibe: 'A team that combines high-precision flying with a light-hearted approach.',
    },
    // Membership caveat was submitted inside the `recurring` field; moved here.
    pitch:
      'Membership in Blue Tide is by invitation only. Practices are open to any JTF Heavy ' +
      'member to observe, but flying is limited to team members.',
    _confirmed: true,
  },
  '23rd-aggressors-squadron': draft(
    'Adversary air and red air training',
    ['Aggressor / red air', 'DACT'],
  ),
  'phantom-logistics': draft(
    'Civil and logistics operations',
    ['Transport', 'Logistics'],
  ),
};

// ---------------------------------------------------------------------------
// Derived views, computed from KNOWLEDGE so they stay true as responses arrive.
// Never hand-maintain these lists.
//
// Written without spread syntax or Array.from, for the same Babel-helper reason
// documented on `draft` above — this file must stay analysable as plain CJS.

// Every named qualification offered anywhere in the task force, de-duplicated,
// in the order squadrons are listed.
const allQuals = () => {
  const out = [];
  Object.keys(KNOWLEDGE).forEach((slug) => {
    (KNOWLEDGE[slug].quals || []).forEach((q) => {
      if (out.indexOf(q) === -1) out.push(q);
    });
  });
  return out;
};

// Flat list of every recurring event across the force, tagged with its squadron.
const trainingSchedule = () => {
  const out = [];
  Object.keys(KNOWLEDGE).forEach((slug) => {
    (KNOWLEDGE[slug].recurring || []).forEach((r) => {
      out.push({ slug: slug, name: r.name, cadence: r.cadence });
    });
  });
  return out;
};

// Mission tags actually flown by at least one squadron, in MISSION_TAGS order.
const activeMissionTags = () => {
  return MISSION_TAGS.filter((tag) =>
    Object.keys(KNOWLEDGE).some((slug) => (KNOWLEDGE[slug].missions || []).indexOf(tag) !== -1)
  );
};

// How many squadrons a lead has signed off on — used to keep claims honest.
const confirmedCount = () =>
  Object.keys(KNOWLEDGE).filter((slug) => KNOWLEDGE[slug]._confirmed).length;

module.exports = {
  KNOWLEDGE,
  MISSION_TAGS,
  FALLBACK_PITCH,
  allQuals,
  trainingSchedule,
  activeMissionTags,
  confirmedCount,
};
